export default function Privacy() {
  return (
    <main className="page">
      <div className="page-content">
        <div className="card">
          <h1 className="card-title">Privacy Policy</h1>
          <p className="card-subtitle">Last Updated: 9/9/2026</p>

          <section className="card-section">
            <p>
              Cross Chat (&quot;we,&quot; &quot;our,&quot; &quot;us&quot;) operates a Discord bot that connects
              participating Discord communities through shared cross-server chat channels. This Privacy Policy explains
              how we collect, use, store, and protect information when you use Cross Chat.
            </p>
            <p>
              By using Cross Chat, you agree to the practices described in this Privacy Policy.
            </p>
          </section>

          <section className="card-section">
            <h2>1. Information We Collect</h2>

            <h3>1.1 Discord Account and Server Information</h3>
            <ul>
              <li>
                <strong>User Information:</strong> Discord user IDs, usernames, display names, avatars, and other basic
                account information needed to identify users within Cross Chat.
              </li>
              <li>
                <strong>Guild (Server) Information:</strong> Server IDs, server names, channel IDs, configuration
                settings, enabled Cross Chat channels, and related server settings.
              </li>
              <li>
                <strong>Membership and Role Information:</strong> Server membership, join timestamps, roles, and
                permissions as needed for eligibility checks, moderation, verification, and role-based titles or access.
              </li>
            </ul>

            <h3>1.2 Message and Interaction Information</h3>
            <ul>
              <li>
                <strong>Cross Chat Messages:</strong> Message content sent in configured Cross Chat channels is processed
                so the bot can apply moderation rules and relay approved messages to participating servers.
              </li>
              <li>
                <strong>Direct Messages:</strong> Messages sent directly to the bot may be used for support,
                verification, moderation requests, or communication with Cross Chat staff.
              </li>
              <li>
                <strong>Support and Moderation Records:</strong> Support conversations, moderation actions, staff
                responses, and related transcripts may be retained when needed for administration or abuse prevention.
              </li>
            </ul>

            <h3>1.3 Cross Chat Account Data</h3>
            <ul>
              <li>
                <strong>Bot-Specific User Data:</strong> Verification status, XP, levels, ranks, titles, cooldowns,
                blocks, eligibility status, and other information needed for Cross Chat features.
              </li>
              <li>
                <strong>Moderation and Configuration Data:</strong> Blocked users, filtered words or strings, server
                configuration, and other settings used to operate and moderate Cross Chat.
              </li>
            </ul>

            <p>
              We do not collect Discord passwords or other private Discord account credentials.
            </p>
          </section>

          <section className="card-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect strictly to operate and maintain Cross Chat, including to:</p>
            <ul>
              <li>Relay approved messages between participating Discord servers.</li>
              <li>
                Apply moderation rules such as blocked-user checks, word and string filters, link filtering, spam
                controls, cooldowns, and message-length restrictions.
              </li>
              <li>Determine whether users meet membership or verification requirements for Cross Chat access.</li>
              <li>Apply role-based permissions, staff indicators, titles, ranks, and other Cross Chat features.</li>
              <li>Operate XP, level, verification, ranking, and custom-title systems.</li>
              <li>Route user support and verification requests to authorized Cross Chat staff.</li>
              <li>Investigate abuse, enforce Cross Chat rules, and maintain the security and reliability of the bot.</li>
              <li>Debug technical problems and improve the performance of Cross Chat.</li>
            </ul>
            <p>
              We <strong>do not</strong> sell your personal information, and we <strong>do not</strong> share it with
              third parties for advertising or marketing purposes.
            </p>
          </section>

          <section className="card-section">
            <h2>3. Message Content</h2>
            <p>
              Cross Chat must process message content sent in configured Cross Chat channels in order to provide its
              core functionality. Before a message is relayed, the bot may inspect the content for moderation rules,
              including prohibited words or strings, links, spam, and message-length limits.
            </p>
            <p>
              Approved messages may then be displayed in other participating Discord servers as part of the shared
              Cross Chat conversation.
            </p>
            <p>
              Cross Chat does <strong>not</strong> use message content to train machine learning or artificial
              intelligence models.
            </p>
          </section>

          <section className="card-section">
            <h2>4. Data Storage &amp; Security</h2>
            <ul>
              <li>
                Some Discord identifiers, Cross Chat account data, moderation records, and configuration settings are
                stored in an external database used by the bot.
              </li>
              <li>
                Support or moderation transcripts may also be temporarily stored by the system hosting the bot and/or
                posted in staff-only Discord channels.
              </li>
              <li>
                Access to stored data and administrative systems is limited to authorized systems and administrators.
              </li>
              <li>
                We use reasonable technical and organizational measures to protect information from unauthorized access,
                misuse, or disclosure.
              </li>
            </ul>
            <p>No online service or storage system can guarantee absolute security.</p>
          </section>

          <section className="card-section">
            <h2>5. Data Retention</h2>
            <ul>
              <li>
                Cross Chat account data may be retained for as long as it is needed to provide bot features, preserve
                moderation settings, or maintain user verification, rank, title, or eligibility status.
              </li>
              <li>
                Guild configuration data may be retained while Cross Chat remains associated with a participating
                server and may be removed when it is no longer needed.
              </li>
              <li>
                Support and moderation records may be retained as needed for administration, abuse prevention, security,
                or dispute resolution.
              </li>
              <li>
                You may contact us to request deletion of personal data associated with your Discord ID where
                technically and legally possible.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>6. Data Sharing</h2>
            <p>We only share or disclose data in limited circumstances:</p>
            <ul>
              <li>
                <strong>Participating Discord Servers:</strong> Messages submitted to Cross Chat may be relayed to other
                participating servers as part of the service.
              </li>
              <li>
                <strong>Authorized Staff:</strong> Information may be visible to authorized Cross Chat staff when needed
                for moderation, verification, support, or administration.
              </li>
              <li>
                <strong>Discord:</strong> Cross Chat interacts with Discord&apos;s API and platform in order to operate
                the bot.
              </li>
              <li>
                <strong>Hosting/Infrastructure Providers:</strong> Data may be processed by the hosting, database, or
                infrastructure providers used to operate Cross Chat.
              </li>
              <li>
                <strong>Legal Requirements:</strong> Information may be disclosed if required to comply with applicable
                laws, regulations, or valid legal requests.
              </li>
            </ul>
            <p>We do not otherwise share or sell user information.</p>
          </section>

          <section className="card-section">
            <h2>7. Children&apos;s Privacy</h2>
            <p>
              Cross Chat is intended only for users who are permitted to use Discord under Discord&apos;s applicable
              age requirements. We do not knowingly collect information from users who are not permitted to use Discord.
            </p>
          </section>

          <section className="card-section">
            <h2>8. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul>
              <li>Request access to personal data we store about you.</li>
              <li>Request correction or deletion of your personal data.</li>
              <li>Ask questions about how your data is collected, stored, and used.</li>
            </ul>
            <p>
              To make a request, send a direct message to the Cross Chat bot and explain that you are making a privacy or
              data deletion request. We may need to verify your identity through your Discord account before fulfilling
              the request.
            </p>
            <p>
              Some information may be retained when reasonably necessary for security, abuse prevention, moderation,
              legal compliance, or the integrity of the service.
            </p>
          </section>

          <section className="card-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the &quot;Last
              Updated&quot; date at the top of this page. Continued use of Cross Chat after changes are posted means you
              accept the updated policy.
            </p>
          </section>

          <section className="card-section">
            <h2>10. Contact Us</h2>
            <p>
              If you have questions, privacy requests, or data deletion requests, please send a direct message to the
              Cross Chat bot through Discord. Your message will be routed to Cross Chat staff for review.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}
